import { useCallback, useMemo, useState } from "react"

import { useMatch, useParams } from "react-router-dom"

import { API_MODULE_DOCUMENT, DocumentEditModal } from "@l2r-front/l2r-app-doc"
import { CatalogItemEditModal } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, EditLocationIcon, ModeEditIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { APP_CITY } from "../../../../common/constants/appCity"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { MODULE_EQUIPMENT } from "../../../../common/constants/modules"
import { DETAIL_TAB_PARAM_KEY } from "../../../../common/constants/tabKeys"
import { EquipmentAddDocumentForm } from "../../forms/EquipmentAddDocumentForm"
import { EquipmentDetailsFormPost } from "../../forms/EquipmentDetailsForm"
import { EquipmentLocationEditFormPost } from "../../forms/EquipmentLocationEditForm"
import { useEquipmentDelete } from "../../hooks/mutations/useEquipmentDelete"
import { useEquipment } from "../../hooks/queries/equipment/useEquipments"
import { useEquipmentType } from "../../hooks/queries/equipment/useEquipmentsType"

import { EquipmentDetailsSidebarContent } from "../EquipmentDetailsSidebarContent"

import * as Styled from "./EquipmentDetailSidebar.styled"

export function EquipmentDetailSidebar() {

    const [localizationModalOpen, setLocalizationModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)

    const { setAlert } = useAlertsDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()
    const { signId: equipmentId, road } = useParams()
    const readOnly = useRoleIsReadOnly()
    const { selectedNetwork } = useNetworksStateContext()

    const { data: equipment, isLoading: isLoadingEquipment } = useEquipment(equipmentId)
    const { data: equipmentType } = useEquipmentType(equipment?.equipment_type, {}, { enabled: !!equipment })
    const isMobile = useIsMobileDevice()
    const matchRoadCode = useMatch(`:slugId/${APP_CITY}/${MODULE_EQUIPMENT}/:road/code/:code/*`)

    const latLong = equipment?.geometry.coordinates

    const title = useMemo(() => {
        return equipmentType?.name ? equipmentType.name : ""
    }, [equipmentType])

    const navigateBack = useCallback(() => {
        navigate(matchRoadCode ? `../${road}/code/${matchRoadCode.params.code}` : `../${road}`, { paramsToDelete: [DETAIL_TAB_PARAM_KEY], navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [matchRoadCode, navigate, road])

    const deleteMutation = useEquipmentDelete(equipmentId, {
        onSuccess: () => navigate(`../${road}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.success"),
    })

    const handleDeleteEquipment = useCallback(() => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.confirmation", { itemName: equipmentType?.name }),
            cancelText: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.cancel"),
            confirmText: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.confirm"),
            callback: () => {
                deleteMutation.mutate()
            },
            severity: "warning",
        })
    }, [equipmentType, deleteMutation, setAlert, t])

    if (isLoadingEquipment || !equipment) {
        return null
    }

    return <>
        <Styled.BottomSheetContent id="equipmentDetailSidebar-sidebar"
            isMobile={isMobile}
            onBack={navigateBack}
            title={title}
            titleSideComponent={<MoreMenu
                menuConfig={[
                    {
                        enabled: !readOnly,
                        id: "edit-location",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.location"),
                        icon: <EditLocationIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setLocalizationModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "edit-properties",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.properties", { itemName: equipmentType?.name }),
                        icon: <ModeEditIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        enabled: selectedNetwork.modules.includes(API_MODULE_DOCUMENT) && !readOnly,
                        id: "add-document",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.addDocument"),
                        icon: <NoteAddRoundedIcon color="objects/object-black" />,
                        props: {
                            onClick: () => setDocumentAddModalOpen(true),
                        },
                    },
                    {
                        enabled: !readOnly,
                        id: "delete-equipment",
                        label: t(I18N_NAMESPACE, "containers.equipmentDetailSidebar.edit.delete.menu", { itemName: equipmentType?.name }),
                        icon: <DeleteIcon color="objects/object-black" />,
                        props: {
                            onClick: () => handleDeleteEquipment(),
                        },
                    },
                ]}
            />}
        >
            <EquipmentDetailsSidebarContent equipmentType={equipmentType} />
        </Styled.BottomSheetContent>
        <CatalogItemEditModal
            FormComponent={EquipmentLocationEditFormPost}
            formTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.title")}
            signId={equipmentId}
            startingPosition={{ longitude: latLong[0], latitude: latLong[1] }}
            startingRoad={equipment?.linear_location.road}
            open={localizationModalOpen}
            onClose={() => setLocalizationModalOpen(false)}
        />
        <CatalogItemEditModal
            formTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.title")}
            typeTitle={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.type")}
            contentFieldLabel={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.content")}
            FormComponent={EquipmentDetailsFormPost}
            signId={equipmentId}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />
        {documentAddModalOpen && <DocumentEditModal
            open={documentAddModalOpen}
            onClose={() => setDocumentAddModalOpen(false)}
            FormComponent={EquipmentAddDocumentForm}
            equipmentId={equipmentId}
        />}
    </>
}